import { format, parseISO } from 'date-fns';
import { Chip } from '@ds';
import { parseZonedDateTime } from '@internationalized/date';
import { SchedulerNotificationData, NotificationMessageType, NotificationType } from '@/api/apis/communication';
import { getColorByNotificationStatus, getTzDateTimeString } from '@/utils/helpers';
import { caseStatusNames, routeStatusNames } from '@/constants/case/case.constants';
import { CaseStatus, ResourceLogisticsStatus, RoutePlanStatus } from '@/api/apis/case';
import { RESOURCE_STATUS_TEXT_MAP } from '@/constants/resources.constants';
import { BulkNotificationBody } from './components/BulkNotificationBody/BulkNotificationBody';

export const getSchedulerPath = (data: SchedulerNotificationData) => {
  const day = data.PeriodStart ? format(parseISO(data.PeriodStart), 'MM-dd-yyyy') : `${data.Month}-01-${data.Year}`;

  switch (data?.SchedulerType) {
    case 'ClinicalCoordinator':
      return `/schedule/cc?day=${day}`;
    case 'LiSurgeon':
      return `/schedule/s-li?day=${day}`;
    case 'HtLuSurgeon':
      return `/schedule/s-htlu?day=${day}`;
    case 'ClinicalSpecialist':
      return `/schedule/cs?day=${day}`;
    default:
      return undefined;
  }
};

const defaultCaseDetailsMessage = <span className="text-s-r text-base-5">Click to review the case details</span>;
const statusChangeNotifications = [
  NotificationMessageType.CaseStatusUpdated,
  NotificationMessageType.RoutePlanningStatusUpdated,
  NotificationMessageType.CaseResourceLogisticsStatusUpdated,
];

export const getCustomCaseNotificationBody = (notification: NotificationType) => {
  const { notificationType, data } = notification;
  const newStatus = data?.NewStatus ?? data?.LogisticsStatus;
  const oldStatus = data?.OldStatus;

  if (!newStatus && statusChangeNotifications.includes(notificationType)) {
    return defaultCaseDetailsMessage;
  }

  switch (notificationType) {
    case NotificationMessageType.CaseStatusUpdated:
      return (
        <div className="flex flex-row gap-1 items-center">
          {oldStatus && (
            <>
              <Chip size="sm" className={getColorByNotificationStatus(oldStatus as CaseStatus)}>
                <div className="text-s-sb">{caseStatusNames[oldStatus as CaseStatus]}</div>
              </Chip>
              <span className="text-s-r text-base-5">changed to</span>
            </>
          )}
          <Chip size="sm" className={getColorByNotificationStatus(newStatus as CaseStatus)}>
            <div className="text-s-sb">{caseStatusNames[newStatus as CaseStatus]}</div>
          </Chip>
        </div>
      );
    case NotificationMessageType.RoutePlanningStatusUpdated:
      return (
        <div className="flex flex-row gap-1 items-center">
          <span className="text-s-r text-base-5">Logistic status changed to</span>
          <Chip size="sm" className={getColorByNotificationStatus(newStatus as CaseStatus)}>
            <div className="text-s-sb">{routeStatusNames[newStatus as RoutePlanStatus]}</div>
          </Chip>
        </div>
      );
    case NotificationMessageType.CaseResourceLogisticsStatusUpdated:
      return (
        <div className="flex flex-row gap-1 items-center">
          <span className="text-s-r text-base-5">Resource status changed to</span>
          <Chip size="sm" className={getColorByNotificationStatus(newStatus as CaseStatus)}>
            <div className="text-s-sb">{RESOURCE_STATUS_TEXT_MAP[newStatus as ResourceLogisticsStatus]}</div>
          </Chip>
        </div>
      );
    case NotificationMessageType.CaseBulkOperationPrimaryElevation:
    case NotificationMessageType.CaseBulkSwapOperationCompleted:
    case NotificationMessageType.CaseBulkAssignOperationCompleted:
    case NotificationMessageType.CaseBulkDetachOperationCompleted:
      return <BulkNotificationBody data={data} notificationType={notificationType} />;
    default:
      return defaultCaseDetailsMessage;
  }
};

const getMonthName = (monthNumber: string) => {
  const date = new Date(2023, Number(monthNumber) - 1);
  return format(date, 'MMMM');
};

const getSchedulerPeriodOrMonthLabel = (data: SchedulerNotificationData) => {
  if (data.PeriodStart && data.PeriodEnd) {
    const start = format(parseISO(data.PeriodStart), 'd MMM yyyy');
    const end = format(parseISO(data.PeriodEnd), 'd MMM yyyy');
    return `${start} – ${end}`;
  }

  return `${getMonthName(data.Month ?? '')} ${data.Year ?? ''}`;
};

const formatSchedulerType = (schedulerType: string) => {
  switch (schedulerType) {
    case 'ClinicalCoordinator':
      return 'Clinical coordinator';
    case 'ClinicalSpecialist':
      return 'Clinical specialist';
    case 'LiSurgeon':
      return 'Liver surgeon';
    case 'HtLuSurgeon':
      return 'Heart & Lungs surgeon';
    default:
      return schedulerType;
  }
};

export const getBodyForSchedulerNotificationType = (data: SchedulerNotificationData, createdAt: string) => {
  const formattedCreatedAt = getTzDateTimeString(createdAt);

  switch (data.SchedulerNotificationType) {
    case 'ScheduleProposed':
      return (
        <div className="flex flex-row gap-1 items-center">
          <span className="text-m-m text-base-5">
            {formatSchedulerType(data.SchedulerType)} schedule for {getSchedulerPeriodOrMonthLabel(data)} generated
            successfully
          </span>
        </div>
      );
    case 'ScheduleGenerationFailed':
      return (
        <div className="flex flex-row gap-1 items-center">
          <span className="text-m-m text-base-5">
            {formatSchedulerType(data.SchedulerType)} schedule for {getSchedulerPeriodOrMonthLabel(data)} generation
            failed. The current settings and inputs do not allow it.
          </span>
        </div>
      );
    case 'SchedulePublished':
      return (
        <div className="flex flex-row gap-1 items-center">
          <span className="text-m-m text-base-5">
            {formatSchedulerType(data.SchedulerType)} schedule for {getSchedulerPeriodOrMonthLabel(data)} published{' '}
            {formattedCreatedAt} by {data?.UserFullName}.
          </span>
        </div>
      );
    case 'SchedulePlanningProcessCreated':
      return (
        <div className="flex flex-row gap-1 items-center">
          <span className="text-m-m text-base-5">
            {formatSchedulerType(data.SchedulerType)} schedule planning process for{' '}
            {getSchedulerPeriodOrMonthLabel(data)} started by {data?.UserFullName}.
          </span>
        </div>
      );
    case 'PlanningProcessCancelled':
      return (
        <div className="flex flex-row gap-1 items-center">
          <span className="text-m-m text-base-5">
            {formatSchedulerType(data.SchedulerType)} schedule planning process for{' '}
            {getSchedulerPeriodOrMonthLabel(data)} discarded by {data?.UserFullName}.
          </span>
        </div>
      );
    default:
      return undefined;
  }
};

export const formatCreatedAt = (createdAt: string) => {
  const timeZoneDate = getTzDateTimeString(createdAt);
  const utcString = parseZonedDateTime(timeZoneDate).toAbsoluteString();

  try {
    const date = new Date(utcString);
    return format(date, 'M/d/yyyy, HH:mm:ss aa');
  } catch {
    return '';
  }
};
